import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';



// Import components
import UserCard from './components/UserCard';
import UserDataGrid from './components/UserDataGrid';
import RoleManagementPanel from './components/RoleManagementPanel';
import SessionManagementSection from './components/SessionManagementSection';
import UserDetailsDrawer from './components/UserDetailsDrawer';
import IntegrationStatusPanel from './components/IntegrationStatusPanel';

const UserManagementConsole = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  // Mock users data
  useEffect(() => {
    const mockUsers = [
      {
        id: 1,
        name: 'Arjun Sharma',
        email: 'arjun.sharma@company.com',
        department: 'IT',
        role: 'IT Admin',
        status: 'Active',
        lastLogin: new Date(Date.now() - 2 * 60 * 60 * 1000),
        createdAt: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000),
        employeeId: 'EMP001',
        loginCount: 245,
        failedAttempts: 0
      },
      {
        id: 2,
        name: 'Priya Patel',
        email: 'priya.patel@company.com',
        department: 'Finance',
        role: 'Asset Manager',
        status: 'Active',
        lastLogin: new Date(Date.now() - 4 * 60 * 60 * 1000),
        createdAt: new Date(Date.now() - 150 * 24 * 60 * 60 * 1000),
        employeeId: 'EMP002',
        loginCount: 189,
        failedAttempts: 1
      },
      {
        id: 3,
        name: 'Rahul Verma',
        email: 'rahul.verma@company.com',
        department: 'Operations',
        role: 'Auditor',
        status: 'Active',
        lastLogin: new Date(Date.now() - 6 * 60 * 60 * 1000),
        createdAt: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000),
        employeeId: 'EMP003',
        loginCount: 156,
        failedAttempts: 0
      },
      {
        id: 4,
        name: 'Anjali Singh',
        email: 'anjali.singh@company.com',
        department: 'HR',
        role: 'Standard User',
        status: 'Inactive',
        lastLogin: new Date(Date.now() - 48 * 60 * 60 * 1000),
        createdAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
        employeeId: 'EMP004',
        loginCount: 78,
        failedAttempts: 2
      },
      {
        id: 5,
        name: 'Vikram Nair',
        email: 'vikram.nair@company.com',
        department: 'Marketing',
        role: 'Asset Manager',
        status: 'Active',
        lastLogin: new Date(Date.now() - 12 * 60 * 60 * 1000),
        createdAt: new Date(Date.now() - 75 * 24 * 60 * 60 * 1000),
        employeeId: 'EMP005',
        loginCount: 134,
        failedAttempts: 0
      },
      {
        id: 6,
        name: 'Neha Gupta',
        email: 'neha.gupta@company.com',
        department: 'Sales',
        role: 'Standard User',
        status: 'Suspended',
        lastLogin: new Date(Date.now() - 72 * 60 * 60 * 1000),
        createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
        employeeId: 'EMP006',
        loginCount: 45,
        failedAttempts: 5
      },
      {
        id: 7,
        name: 'Suresh Reddy',
        email: 'suresh.reddy@company.com',
        department: 'IT',
        role: 'Asset Manager',
        status: 'Active',
        lastLogin: new Date(Date.now() - 8 * 60 * 60 * 1000),
        createdAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
        employeeId: 'EMP007',
        loginCount: 89,
        failedAttempts: 0
      },
      {
        id: 8,
        name: 'Kavita Joshi',
        email: 'kavita.joshi@company.com',
        department: 'Finance',
        role: 'Auditor',
        status: 'Pending',
        lastLogin: null,
        createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        employeeId: 'EMP008',
        loginCount: 0,
        failedAttempts: 0
      }
    ];

    setUsers(mockUsers);
  }, []);

  const tabs = [
    { id: 'users', label: 'User Directory', icon: 'Users' },
    { id: 'roles', label: 'Role Management', icon: 'Shield' },
    { id: 'sessions', label: 'Active Sessions', icon: 'Monitor' },
    { id: 'integrations', label: 'Integrations', icon: 'Zap' }
  ];

  const handleUserSelect = (userId) => {
    setSelectedUsers(prev => 
      prev?.includes(userId) 
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = (filteredUsers) => {
    const allSelected = filteredUsers?.every(user => selectedUsers?.includes(user?.id));
    if (allSelected) {
      setSelectedUsers(prev => prev?.filter(id => !filteredUsers?.some(user => user?.id === id)));
    } else {
      const newSelections = filteredUsers?.map(user => user?.id);
      setSelectedUsers(prev => [...new Set([...prev, ...newSelections])]);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsDrawerOpen(true);
  };

  const handleToggleUserStatus = (user) => {
    const newStatus = user?.status === 'Active' ? 'Suspended' : 'Active';
    setUsers(prev =>
      prev?.map(u =>
        u?.id === user?.id ? { ...u, status: newStatus } : u
      )
    );
  };

  const handleBulkAction = (action) => {
    switch (action) {
      case 'activate':
        setUsers(prev =>
          prev?.map(user =>
            selectedUsers?.includes(user?.id) ? { ...user, status: 'Active' } : user
          )
        );
        break;
      case 'suspend':
        setUsers(prev =>
          prev?.map(user =>
            selectedUsers?.includes(user?.id) ? { ...user, status: 'Suspended' } : user
          )
        );
        break;
      case 'reset-password': console.log('Password reset for users:', selectedUsers);
        break;
      default:
        break;
    }
    setSelectedUsers([]);
  };

  const handleSaveUser = (userData) => {
    setUsers(prev =>
      prev?.map(user =>
        user?.id === userData?.id ? { ...user, ...userData } : user
      )
    );
    setIsDrawerOpen(false);
  };

  const handleViewUserDetails = (user) => {
    setSelectedUser(user);
    setIsDrawerOpen(true);
  };

  const handleCreateRole = (roleData) => {
    console.log('Creating role:', roleData);
  };

  const handleUpdateRole = (roleId, updates) => {
    console.log('Updating role:', roleId, updates);
  };

  const handleDeleteRole = (roleId) => {
    console.log('Deleting role:', roleId);
  };

  const handleTerminateSession = (sessionId) => {
    console.log('Terminating session:', sessionId);
  };

  const handleTerminateAllSessions = () => {
    console.log('Terminating all sessions');
  };

  const handleSyncNow = (integrationId) => {
    console.log('Syncing integration:', integrationId);
  };

  const handleConfigureIntegration = (integrationId) => {
    console.log('Configuring integration:', integrationId);
  };

  const getTabContent = () => {
    switch (activeTab) {
      case 'users':
        return (
          <div className="space-y-6">
            {/* User Management Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-foreground">User Directory</h2>
                <p className="text-muted-foreground mt-1">
                  Manage user accounts, roles, and permissions across the organization
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    iconName="Grid3X3"
                  />
                  <Button
                    variant={viewMode === 'table' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('table')}
                    iconName="List"
                  />
                </div>
                <Button
                  variant="default"
                  onClick={() => setIsCreatingUser(true)}
                  iconName="UserPlus"
                  iconPosition="left"
                >
                  Add User
                </Button>
              </div>
            </div>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="Users" size={24} className="text-success" />
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-card-foreground">
                      {users?.filter(u => u?.status === 'Active')?.length}
                    </div>
                    <div className="text-sm text-muted-foreground">Active Users</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                    <Icon name="UserX" size={24} className="text-warning" />
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-card-foreground">
                      {users?.filter(u => u?.status === 'Suspended')?.length}
                    </div>
                    <div className="text-sm text-muted-foreground">Suspended</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="Clock" size={24} className="text-accent" />
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-card-foreground">
                      {users?.filter(u => u?.status === 'Pending')?.length}
                    </div>
                    <div className="text-sm text-muted-foreground">Pending</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Shield" size={24} className="text-primary" />
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-card-foreground">4</div>
                    <div className="text-sm text-muted-foreground">System Roles</div>
                  </div>
                </div>
              </div>
            </div>
            {/* User Content */}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {users?.map((user) => (
                  <UserCard
                    key={user?.id}
                    user={user}
                    onEdit={handleEditUser}
                    onToggleStatus={handleToggleUserStatus}
                    onViewDetails={handleViewUserDetails}
                  />
                ))}
              </div>
            ) : (
              <UserDataGrid
                users={users}
                onEdit={handleEditUser}
                onToggleStatus={handleToggleUserStatus}
                onBulkAction={handleBulkAction}
                selectedUsers={selectedUsers}
                onUserSelect={handleUserSelect}
                onSelectAll={handleSelectAll}
              />
            )}
          </div>
        );

      case 'roles':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-foreground">Role Management</h2>
              <p className="text-muted-foreground mt-1">
                Configure system roles and permissions for access control
              </p>
            </div>
            <RoleManagementPanel
              onCreateRole={handleCreateRole}
              onUpdateRole={handleUpdateRole}
              onDeleteRole={handleDeleteRole}
            />
          </div>
        );

      case 'sessions':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-foreground">Session Management</h2>
              <p className="text-muted-foreground mt-1">
                Monitor and manage active user sessions across the system
              </p>
            </div>
            <SessionManagementSection
              onTerminateSession={handleTerminateSession}
              onTerminateAllSessions={handleTerminateAllSessions}
            />
          </div>
        );

      case 'integrations':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-foreground">System Integrations</h2>
              <p className="text-muted-foreground mt-1">
                Monitor external system connections and data synchronization
              </p>
            </div>
            <IntegrationStatusPanel
              onSyncNow={handleSyncNow}
              onConfigureIntegration={handleConfigureIntegration}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'}`}>
        <div className="p-6">
          <Breadcrumb />
          
          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-smooth ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {getTabContent()}
        </div>
      </main>
      {/* User Details Drawer */}
      <UserDetailsDrawer
        user={selectedUser}
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedUser(null);
        }}
        onSave={handleSaveUser}
      />
      {/* Footer */}
      <footer className={`bg-card border-t border-border py-4 transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'}`}>
        <div className="px-6">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center space-x-4">
              <span>© {new Date()?.getFullYear()} AssetTracker Pro</span>
              <span>•</span>
              <span>User Management Console</span>
            </div>
            <div className="flex items-center space-x-4">
              <span>Last updated: Aug 19, 2025</span>
              <span>•</span>
              <span>{users?.length} total users</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserManagementConsole;